import { useState, useEffect, useRef } from 'react';
import { fetchDirectoryUserAvatar } from '../../../../../../ms-teams-sdk/service/MsTeamsUserService';
import { ITranscriptEntry } from './AISummary.types';

/**
 * Resolves avatar URLs for unique speakers in transcript entries.
 * Uses speakerEmail to fetch photos from MS Graph API.
 * Returns a map of speakerEmail -> blob URL.
 */
export function useTranscriptAvatars(entries: ITranscriptEntry[]): Record<string, string> {
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});
  const prevUrlsRef = useRef<string[]>([]);

  // Collect unique emails from entries
  const uniqueEmails = Array.from(
    new Set(entries.map((e) => e.speakerEmail).filter(Boolean) as string[])
  );
  const emailsKey = uniqueEmails.sort().join(',');

  useEffect(() => {
    if (uniqueEmails.length === 0) return;

    let cancelled = false;

    (async () => {
      const results: Record<string, string> = {};

      // Fetch all avatars in parallel — fetchDirectoryUserAvatar accepts email as user identifier
      const fetches = uniqueEmails.map(async (email) => {
        try {
          const url = await fetchDirectoryUserAvatar(email);
          if (url) results[email] = url;
        } catch {
          // Avatar not available — initials fallback handled by caller
        }
      });

      await Promise.all(fetches);
      if (!cancelled) {
        // Revoke previous blob URLs before setting new ones
        prevUrlsRef.current.forEach((url) => {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        });
        prevUrlsRef.current = Object.values(results);
        setAvatarMap(results);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [emailsKey]);

  // Revoke remaining blob URLs on unmount
  useEffect(() => {
    return () => {
      prevUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      });
    };
  }, []);

  return avatarMap;
}
